import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { SettingsService } from './settings.service';
import { ConfirmDialogService } from '../../ui/confirm-dialog/confirm-dialog.service';
import { AccountStatus } from '../../api/gqty/schema.generated';

/**
 * Thrown when the backend refuses an account deletion because it would remove
 * the tenant's last active administrator. Callers can special-case this to show
 * the server's explanatory message instead of a generic delete-failed toast.
 */
export class LastAdminDeletionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LastAdminDeletionError';
  }
}

/** Plain snapshot of a `deleteAccount` payload's first error. */
export interface DeleteAccountErrorInfo {
  typename: string | null;
  lastAdminMessage: string | null;
  otherMessage: string | null;
}

/**
 * Reads a `deleteAccount` mutation payload's first error into a plain object.
 * MUST be called INSIDE the gqty resolver: it accesses every union member's
 * message unconditionally so gqty keeps them in the selection. It does NOT
 * throw — throwing inside the resolver would abort the mutation during gqty's
 * selection pass, so no request is ever sent. Interpret the returned value with
 * {@link throwIfDeleteBlocked} AFTER `resolve()` settles.
 */
export function readDeleteAccountError(
  errors:
    | ReadonlyArray<{
        __typename?: string | null;
        $on: {
          LastAdminError?: { message?: string | null } | null;
          NotFoundError?: { message?: string | null } | null;
        };
      }>
    | null
    | undefined
): DeleteAccountErrorInfo {
  const first = errors?.[0];
  // Access every member's message so gqty resolves them into the query.
  const lastAdminMessage = first?.$on.LastAdminError?.message ?? null;
  const otherMessage = first?.$on.NotFoundError?.message ?? null;
  return { typename: first?.__typename ?? null, lastAdminMessage, otherMessage };
}

/**
 * Throws when a decoded `deleteAccount` payload signals the deletion was
 * blocked. A `LastAdminError` becomes a typed {@link LastAdminDeletionError}
 * (carrying the backend's message); any other error becomes a plain Error.
 * Thrown messages stay empty when the backend gave none, so callers show their
 * own copy rather than a raw i18n key. Call this OUTSIDE the gqty resolver, on
 * the value {@link readDeleteAccountError} returned.
 */
export function throwIfDeleteBlocked(info: DeleteAccountErrorInfo): void {
  if (!info.typename) return;
  if (info.typename === 'LastAdminError') {
    throw new LastAdminDeletionError(info.lastAdminMessage ?? '');
  }
  throw new Error(info.otherMessage ?? '');
}

/** Resend invitation applies only to accounts that never completed onboarding. */
export function canResendInvitation(status: AccountStatus): boolean {
  return (
    status === AccountStatus.PENDING_INVITATION || status === AccountStatus.INVITATION_EXPIRED
  );
}

/** Forcing a password reset only makes sense once the account is active. */
export function canForcePasswordReset(status: AccountStatus): boolean {
  return status === AccountStatus.ACTIVE;
}

/**
 * Shared admin actions for the account list tabs (administrators / managers /
 * workers): confirm, call the mutation, toast the outcome. Each returns whether
 * the action ran so the caller can refresh its list.
 */
@Injectable({ providedIn: 'root' })
export class AccountActionsService {
  private readonly settings = inject(SettingsService);
  private readonly message = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  async forcePasswordReset(account: { id: string; email: string }): Promise<boolean> {
    const ok = await this.confirmDialog.confirm({
      message: this.translate.instant('settings.accounts.forceResetConfirm', { email: account.email }),
    });
    if (!ok) return false;
    try {
      await this.settings.forcePasswordReset(account.id);
      this.message.add({
        severity: 'success',
        summary: this.translate.instant('settings.accounts.forceResetSuccess', { email: account.email }),
      });
      return true;
    } catch (e) {
      this.toastError(e);
      return false;
    }
  }

  async resendInvitation(account: { id: string; email: string }): Promise<boolean> {
    const ok = await this.confirmDialog.confirm({
      message: this.translate.instant('settings.accounts.resendConfirm', { email: account.email }),
    });
    if (!ok) return false;
    try {
      await this.settings.resendAccountInvitation(account.id);
      this.message.add({
        severity: 'success',
        summary: this.translate.instant('settings.accounts.resendSuccess', { email: account.email }),
      });
      return true;
    } catch (e) {
      this.toastError(e);
      return false;
    }
  }

  private toastError(e: unknown): void {
    this.message.add({
      severity: 'error',
      summary: this.translate.instant('common.error'),
      detail:
        e instanceof Error && e.message
          ? e.message
          : this.translate.instant('settings.accounts.actionError'),
      life: 5000,
    });
  }
}
